import React, { useState, useEffect } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from '../../Providers/FloatMessage';
import { GetAdminConfigBL, UpdateAdminConfigBL } from '../../../utilities/BL/PersonalNoisemeter';

/**
 * Modal de Configuración Administrativa de Grito Mexicano (PersonalNoisemeter)
 */
export const PersonalNoisemeterConfigModal = ({
    isOpen,
    onClose,
    roomId = null,
    onConfigUpdated = null
}) => {
    const { showMessage } = useFloatMessage();

    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Estados del formulario
    const [durationSeconds, setDurationSeconds] = useState(5);
    const [ticketPrefix, setTicketPrefix] = useState('GM');
    const [isActive, setIsActive] = useState(true);
    const [maskParticipantNamesInRanking, setMaskParticipantNamesInRanking] = useState(true);
    const [enableAmbientNoiseCalibration, setEnableAmbientNoiseCalibration] = useState(false);
    const [allowMultipleParticipantsOnSameDevice, setAllowMultipleParticipantsOnSameDevice] = useState(false);
    const [enableScoreFormula, setEnableScoreFormula] = useState(false);
    const [primaryColor, setPrimaryColor] = useState('#006847');
    const [backgroundColor, setBackgroundColor] = useState('#121212');
    const [headerTitle, setHeaderTitle] = useState('¡Grito Mexicano!');
    const [headerSubtitle, setHeaderSubtitle] = useState('Demuestra la fuerza de tu orgullo patrio');

    // Cargar configuración existente al abrir
    useEffect(() => {
        if (!isOpen) return;

        const loadConfig = async () => {
            setIsLoading(true);
            try {
                const res = await GetAdminConfigBL(roomId);
                if (res.success && res.data) {
                    const cfg = res.data;
                    setDurationSeconds(cfg.durationSeconds || 5);
                    setTicketPrefix(cfg.ticketPrefix || 'GM');
                    setIsActive(cfg.isActive !== undefined ? cfg.isActive : true);
                    setMaskParticipantNamesInRanking(
                        cfg.maskParticipantNamesInRanking !== undefined ? cfg.maskParticipantNamesInRanking : true
                    );
                    setEnableAmbientNoiseCalibration(
                        cfg.enableAmbientNoiseCalibration !== undefined ? cfg.enableAmbientNoiseCalibration : false
                    );
                    setAllowMultipleParticipantsOnSameDevice(
                        cfg.allowMultipleParticipantsOnSameDevice !== undefined ? cfg.allowMultipleParticipantsOnSameDevice : false
                    );
                    setEnableScoreFormula(
                        cfg.enableScoreFormula !== undefined ? cfg.enableScoreFormula : false
                    );
                    setPrimaryColor(cfg.primaryColor || '#006847');
                    setBackgroundColor(cfg.backgroundColor || '#121212');
                    setHeaderTitle(cfg.headerTitle || '¡Grito Mexicano!');
                    setHeaderSubtitle(cfg.headerSubtitle || 'Demuestra la fuerza de tu orgullo patrio');
                }
            } catch (err) {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: 'Error al consultar la configuración de la sala.'
                });
            } finally {
                setIsLoading(false);
            }
        };

        loadConfig();
    }, [isOpen, roomId, showMessage]);

    // Duraciones rápidas predeterminadas
    const DURATION_CHIPS = [3, 5, 8, 10, 15];

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();

        if (!headerTitle.trim()) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'El título del encabezado es obligatorio.'
            });
            return;
        }

        if (!ticketPrefix.trim()) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'El prefijo del ticket es obligatorio (ej. GM).'
            });
            return;
        }

        const dur = Number(durationSeconds);
        if (isNaN(dur) || dur < 1 || dur > 60) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'La duración debe ser un número entero entre 1 y 60 segundos.'
            });
            return;
        }

        setIsSaving(true);
        try {
            const payload = {
                roomId: roomId || undefined,
                durationSeconds: dur,
                ticketPrefix: ticketPrefix.trim().toUpperCase(),
                isActive,
                maskParticipantNamesInRanking,
                enableAmbientNoiseCalibration,
                allowMultipleParticipantsOnSameDevice,
                enableScoreFormula,
                primaryColor,
                backgroundColor,
                headerTitle: headerTitle.trim(),
                headerSubtitle: headerSubtitle.trim()
            };

            const res = await UpdateAdminConfigBL(payload, roomId);
            if (res.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: '¡Configuración de Grito Mexicano guardada con éxito!'
                });
                if (onConfigUpdated) onConfigUpdated(res.data);
                if (onClose) onClose();
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: res.message || 'No se pudo actualizar la configuración.'
                });
            }
        } catch (err) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de conexión al guardar la configuración.'
            });
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Backdrop */}
            <div
                onClick={onClose}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    backgroundColor: 'rgba(0, 0, 0, 0.75)',
                    zIndex: 99998,
                    backdropFilter: 'blur(4px)'
                }}
            />

            {/* Modal Container */}
            <div
                style={{
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '95%',
                    maxWidth: '760px',
                    maxHeight: '92vh',
                    zIndex: 99999,
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                <div className="card shadow-lg border-0 rounded-4 overflow-hidden text-dark bg-white">
                    {/* Header */}
                    <div
                        className="p-3 px-4 d-flex justify-content-between align-items-center text-white"
                        style={{ backgroundColor: '#006847' }}
                    >
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-gear-fill fs-4 text-warning"></i>
                            <div>
                                <h5 className="mb-0 fw-bold">Configuración de Grito Mexicano</h5>
                                <small className="opacity-75">Ajustes de juego, tiempos, branding y privacidad</small>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            onClick={onClose}
                            disabled={isSaving}
                        ></button>
                    </div>

                    {/* Body */}
                    <div className="card-body p-4 overflow-auto" style={{ maxHeight: '72vh' }}>
                        {isLoading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-success" role="status">
                                    <span className="visually-hidden">Cargando configuración...</span>
                                </div>
                                <p className="text-muted small mt-2">Obteniendo ajustes de la sala...</p>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit}>
                                {/* SECCIÓN 1: REGLAS DE JUEGO */}
                                <h6 className="fw-bold text-success border-bottom pb-2 mb-3">
                                    <i className="bi bi-stopwatch-fill me-2"></i>Tiempos y Participación
                                </h6>

                                <div className="row g-3 mb-4">
                                    {/* Duración */}
                                    <div className="col-12 col-md-6">
                                        <label className="form-label fw-semibold small text-secondary">
                                            Duración del Grito (segundos)
                                        </label>
                                        <div className="input-group mb-2">
                                            <span className="input-group-text bg-light">
                                                <i className="bi bi-clock"></i>
                                            </span>
                                            <input
                                                type="number"
                                                className="form-control fw-bold"
                                                min="1"
                                                max="60"
                                                value={durationSeconds}
                                                onChange={(e) => setDurationSeconds(Math.max(1, Math.min(60, Number(e.target.value) || 1)))}
                                            />
                                            <span className="input-group-text bg-light">seg</span>
                                        </div>
                                        {/* Chips de selección rápida */}
                                        <div className="d-flex flex-wrap gap-1">
                                            <span className="small text-muted me-1 align-self-center">Preajustes:</span>
                                            {DURATION_CHIPS.map((sec) => (
                                                <button
                                                    key={sec}
                                                    type="button"
                                                    onClick={() => setDurationSeconds(sec)}
                                                    className={`btn btn-sm py-0 px-2 rounded-pill fw-semibold ${
                                                        durationSeconds === sec
                                                            ? 'btn-success text-white'
                                                            : 'btn-outline-secondary'
                                                    }`}
                                                    style={{ fontSize: '0.75rem' }}
                                                >
                                                    {sec}s
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Prefijo del Ticket */}
                                    <div className="col-12 col-md-6">
                                        <label className="form-label fw-semibold small text-secondary">
                                            Prefijo del Ticket
                                        </label>
                                        <div className="input-group mb-1">
                                            <span className="input-group-text bg-light">
                                                <i className="bi bi-ticket-perforated"></i>
                                            </span>
                                            <input
                                                type="text"
                                                className="form-control text-uppercase fw-bold"
                                                maxLength="10"
                                                placeholder="GM"
                                                value={ticketPrefix}
                                                onChange={(e) => setTicketPrefix(e.target.value.toUpperCase())}
                                            />
                                        </div>
                                        <span className="text-muted small" style={{ fontSize: '0.75rem' }}>
                                            Ejemplo de ticket generado: <strong>{ticketPrefix || 'GM'}-4821</strong>
                                        </span>
                                    </div>
                                </div>

                                {/* Switches */}
                                <div className="card bg-light border-0 p-3 rounded-3 mb-4">
                                    <div className="row g-3">
                                        <div className="col-12 col-md-3">
                                            <div className="form-check form-switch">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id="switchIsActive"
                                                    checked={isActive}
                                                    onChange={(e) => setIsActive(e.target.checked)}
                                                />
                                                <label className="form-check-label fw-semibold small" htmlFor="switchIsActive">
                                                    Dinámica Activa
                                                </label>
                                                <span className="d-block text-muted small" style={{ fontSize: '0.72rem' }}>
                                                    Permite registros y envíos móviles.
                                                </span>
                                            </div>
                                        </div>

                                        <div className="col-12 col-md-3">
                                            <div className="form-check form-switch">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id="switchMaskNames"
                                                    checked={maskParticipantNamesInRanking}
                                                    onChange={(e) => setMaskParticipantNamesInRanking(e.target.checked)}
                                                />
                                                <label className="form-check-label fw-semibold small" htmlFor="switchMaskNames">
                                                    Enmascarar Nombres
                                                </label>
                                                <span className="d-block text-muted small" style={{ fontSize: '0.72rem' }}>
                                                    Protege PII pública (ej. "Juan P.").
                                                </span>
                                            </div>
                                        </div>

                                        <div className="col-12 col-md-3">
                                            <div className="form-check form-switch">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id="switchCalibration"
                                                    checked={enableAmbientNoiseCalibration}
                                                    onChange={(e) => setEnableAmbientNoiseCalibration(e.target.checked)}
                                                />
                                                <label className="form-check-label fw-semibold small" htmlFor="switchCalibration">
                                                    Calibrar Ruido Base
                                                </label>
                                                <span className="d-block text-muted small" style={{ fontSize: '0.72rem' }}>
                                                    Mide 2s de ruido ambiente previo.
                                                </span>
                                            </div>
                                        </div>

                                        <div className="col-12 col-md-3">
                                            <div className="form-check form-switch">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id="switchMultipleParticipants"
                                                    checked={allowMultipleParticipantsOnSameDevice}
                                                    onChange={(e) => setAllowMultipleParticipantsOnSameDevice(e.target.checked)}
                                                />
                                                <label className="form-check-label fw-semibold small" htmlFor="switchMultipleParticipants">
                                                    Múltiples Registros
                                                </label>
                                                <span className="d-block text-muted small" style={{ fontSize: '0.72rem' }}>
                                                    Varios usuarios en mismo celular.
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* SECCIÓN: CRITERIO DE EVALUACIÓN Y GANADORES */}
                                <h6 className="fw-bold text-success border-bottom pb-2 mb-3">
                                    <i className="bi bi-trophy-fill me-2"></i>Criterio de Evaluación y Clasificación
                                </h6>

                                <div className="card bg-light border-0 p-3 rounded-3 mb-4">
                                    <div className="form-check form-switch mb-2">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="switchScoreFormula"
                                            checked={enableScoreFormula}
                                            onChange={(e) => setEnableScoreFormula(e.target.checked)}
                                        />
                                        <label className="form-check-label fw-bold small text-dark" htmlFor="switchScoreFormula">
                                            Activar fórmula de puntuación ponderada (Score)
                                        </label>
                                    </div>
                                    <div className="text-secondary small" style={{ fontSize: '0.8rem', lineHeight: '1.4' }}>
                                        {enableScoreFormula ? (
                                            <div className="d-flex align-items-center gap-2 text-success fw-semibold">
                                                <i className="bi bi-calculator-fill fs-5"></i>
                                                <span><strong>Fórmula ponderada activa:</strong> Puntuación = (70% Pico dB) + (30% Promedio dB). El ranking evalúa por puntos y muestra métricas de score ponderado.</span>
                                            </div>
                                        ) : (
                                            <div className="d-flex align-items-center gap-2 text-dark">
                                                <i className="bi bi-volume-up-fill fs-5 text-success"></i>
                                                <span><strong>Modo Decibeles Pico (Por defecto):</strong> El grito más fuerte gana (evalúa directamente Decibeles Pico en dB). En caso de empate se considera el promedio dB sostenido. No se muestran puntos confusos en las pantallas.</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* SECCIÓN 2: BRANDING Y COLORES */}
                                <h6 className="fw-bold text-success border-bottom pb-2 mb-3">
                                    <i className="bi bi-palette-fill me-2"></i>Personalización Visual
                                </h6>

                                <div className="row g-3 mb-4">
                                    <div className="col-12 col-md-6">
                                        <label className="form-label fw-semibold small text-secondary">
                                            Color Primario (Acento / Botones)
                                        </label>
                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="color"
                                                className="form-control form-control-color border-0"
                                                value={primaryColor}
                                                onChange={(e) => setPrimaryColor(e.target.value)}
                                            />
                                            <input
                                                type="text"
                                                className="form-control text-uppercase font-monospace"
                                                value={primaryColor}
                                                onChange={(e) => setPrimaryColor(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-6">
                                        <label className="form-label fw-semibold small text-secondary">
                                            Color de Fondo (Tema)
                                        </label>
                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="color"
                                                className="form-control form-control-color border-0"
                                                value={backgroundColor}
                                                onChange={(e) => setBackgroundColor(e.target.value)}
                                            />
                                            <input
                                                type="text"
                                                className="form-control text-uppercase font-monospace"
                                                value={backgroundColor}
                                                onChange={(e) => setBackgroundColor(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* SECCIÓN 3: TEXTOS Y ENCABEZADOS */}
                                <h6 className="fw-bold text-success border-bottom pb-2 mb-3">
                                    <i className="bi bi-type me-2"></i>Textos y Leyendas Públicas
                                </h6>

                                <div className="mb-3">
                                    <label className="form-label fw-semibold small text-secondary">Título Principal</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="¡Grito Mexicano!"
                                        maxLength="200"
                                        value={headerTitle}
                                        onChange={(e) => setHeaderTitle(e.target.value)}
                                        required
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label fw-semibold small text-secondary">Subtítulo / Eslogan</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Demuestra la fuerza de tu orgullo patrio"
                                        maxLength="500"
                                        value={headerSubtitle}
                                        onChange={(e) => setHeaderSubtitle(e.target.value)}
                                    />
                                </div>
                            </form>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="card-footer bg-light p-3 px-4 d-flex justify-content-end gap-2 border-top">
                        <button
                            type="button"
                            className="btn btn-outline-secondary px-4 fw-semibold"
                            onClick={onClose}
                            disabled={isSaving}
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            className="btn btn-success px-4 fw-bold text-white d-flex align-items-center gap-2"
                            onClick={handleSubmit}
                            disabled={isSaving || isLoading}
                        >
                            {isSaving ? (
                                <>
                                    <span className="spinner-border spinner-border-sm" role="status"></span>
                                    <span>Guardando...</span>
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-check2-circle fs-5"></i>
                                    <span>Guardar Configuración</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PersonalNoisemeterConfigModal;
